import { useCallback, useEffect, useState } from 'react';
import { popularDiet, popularDietKey } from '../api/request';
import { formatPercentData } from '../utils';
import { utils, writeFile } from 'xlsx';

const options = {
  legend: {
    data: [],
    icon: 'circle',
    bottom: '0',
    left: 0,
    itemWidth: 10,
    itemHeight: 10,
    textStyle: {
      color: 'rgba(255,255,255, 0.6)',
      fontSize: '12px'
    },
    selectedMode: false
  },
  // tooltip: {
  //   trigger: 'axis',
  //   axisPointer: {
  //     type: 'shadow'
  //   },
  //   confine: true,
  // },
  tooltip: {
    trigger: 'item',
    axisPointer: {
      type: 'shadow'
    },
    confine: true,
  },
  grid: {
    top: '3%',
    left: '0',
    right: '0',
    bottom: '18%',
    containLabel: true
  },
  yAxis: {
    type: 'value',
    axisLabel: {
      color: '#00A32E',
      formatter: '{value}%'
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: '#00A32E',
        width: 0.5
      }
    },
    max: 100,
  },
  xAxis: {
    type: 'category',
    data: [],
    axisTick: {
      show: false
    },
    axisLabel: {
      color: '#00A32E',
      fontSize: 12,
      fontWeight: 'bold',
    },
    axisLine: {
      lineStyle: {
        color: '#00A32E'
      }
    }
  },
  series: []
};

const colors = ['#00A32E', '#FFC000', '#FE544F', '#00A889', '#FE7C7D', '#7705BC'];

const generateExcel = (preData, excelName = 'data') => {
  /* generate worksheet from state */
  const ws = utils.json_to_sheet(preData);
  /* create workbook and append worksheet */
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, 'Sheet1');
  /* export to XLSX */
  writeFile(wb, excelName + '.xlsx');
};

const usePopularDiet = () => {
  const [filter, setFilter] = useState([]);

  const [dataForCountry, setDataForCountry] = useState({});
  const [countryList, setCountryList] = useState([]);

  const [dataForAge, setDataForAge] = useState({});
  const [ageList, setAgeList] = useState([]);

  const [loadingForCountry, setLoadingForCountry] = useState(false);
  const [loadingForAge, setLoadingForAge] = useState(false);

  const getKey = useCallback(async () => {
    const {data = [], status = ''} = await popularDietKey();
    if(status === 'Success') {
      setFilter(data);
    }
  }, []);

  const getChartDataForCountry = useCallback(async () => {
    setLoadingForCountry(true);
    try {
      const {data={}, status=''}  = await popularDiet({split:'country'});
      if(status === 'Success') {
        // format data
        const tempFormatData = formatPercentData(data);
        setDataForCountry(tempFormatData);

        const keyList = Object.keys(data);
        const tempCountryList = [];
        keyList.forEach(item => tempCountryList.push({label: item, value: item}));
        setCountryList(tempCountryList);
      }
    } catch(error) { console.log(error); } finally {
      setLoadingForCountry(false);
    }
  });

  const getChartDataForAge = useCallback(async () => {
    setLoadingForAge(true);
    try {
      const {data={}, status=''}  = await popularDiet({split:'age'});
      if(status === 'Success') {
        // format data
        const tempFormatData = formatPercentData(data);
        setDataForAge(tempFormatData);

        const keyList = Object.keys(data);
        const tempAgeList = [];
        keyList.forEach(item => tempAgeList.push({label: item, value: item}));
        setAgeList(tempAgeList);
      }
    } catch(error) { console.log(error); } finally {
      setLoadingForAge(false);
    }
  });

  const getChartOptionsForCountry = useCallback((curFilter = [], country = []) => {
    // 筛选数据，定顺序， 定颜色
    let legendData = [];
    let xAxisData = [];
    let seriesData = [];

    filter.forEach(item => {
      if(curFilter.includes(item)) {
        legendData.push(item);
      }
    });

    const countryKeys = countryList.map(item => item.value);
    if(country.length) {
      countryKeys.forEach(item => {
        if(country.includes(item)) {
          xAxisData.push(item);
        }
      });
    } else {
      xAxisData = countryKeys;
    }

    const tempObjForFilterKey = {};
    legendData.forEach(item => {
      tempObjForFilterKey[item] = [];
      xAxisData.forEach(subItem => {
        const tempVal = dataForCountry[subItem][item];
        tempObjForFilterKey[item].push(tempVal);
      });
    });
    
    legendData.forEach(item => {
      const index =  filter.findIndex(filterItem => filterItem === item);
      const color = colors[index];
      const temp = {
        name: item,
        type: 'bar',
        stack: 'total',
        emphasis: {
          focus: 'series'
        },
        label: {
          show: true,
          color: '#ffffff',
          formatter: '{c}%'
        },
        tooltip: {
          formatter: (params) => {
            const header = `<div><span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${params.color};"></span>${params.seriesName}:</div>`;
            let body = '';
            xAxisData.forEach((subItem, index) => {
              body += `<div style="display: flex;justify-content: space-between;"><div>${subItem}:</div><span style="margin-left: 20px;">${tempObjForFilterKey[item][index]}%</span></div>`;
            });
            return header + body;
          }
        },
        color: color,
        barWidth: 60,
        data: tempObjForFilterKey[item]
      };
      seriesData.push(temp);
    });
    const tempOptions = JSON.parse(JSON.stringify(options));
    // tempOptions.tooltip.formatter = tooltipFormatter;
    tempOptions.legend.data = legendData;
    tempOptions.xAxis.data = xAxisData;
    tempOptions.series = seriesData;
    return tempOptions;
  }, [dataForCountry, filter, countryList]);

  const getChartOptionsForAge = useCallback((curFilter = [], age = []) => {
    // 筛选数据，定顺序， 定颜色
    let legendData = [];
    let xAxisData = [];
    let seriesData = [];

    filter.forEach(item => {
      if(curFilter.includes(item)) {
        legendData.push(item);
      }
    });

    const ageKeys = ageList.map(item => item.value);
    if(age.length) {
      ageKeys.forEach(item => {
        if(age.includes(item)) {
          xAxisData.push(item);
        }
      });
    } else {
      xAxisData = ageKeys;
    }

    const tempObjForFilterKey = {};
    legendData.forEach(item => {
      tempObjForFilterKey[item] = [];
      xAxisData.forEach(subItem => {
        const tempVal = dataForAge[subItem][item];
        tempObjForFilterKey[item].push(tempVal);
      });
    });
    
    legendData.forEach(item => {
      const index =  filter.findIndex(filterItem => filterItem === item);
      const color = colors[index];
      const temp = {
        name: item,
        type: 'bar',
        stack: 'total',
        emphasis: {
          focus: 'series'
        },
        label: {
          show: true,
          color: '#ffffff',
          formatter: '{c}%'
        },
        tooltip: {
          formatter: (params) => {
            const header = `<div><span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${params.color};"></span>${params.seriesName}:</div>`;
            let body = '';
            xAxisData.forEach((subItem, index) => {
              body += `<div style="display: flex;justify-content: space-between;"><div>${subItem}:</div><span style="margin-left: 20px;">${tempObjForFilterKey[item][index]}%</span></div>`;
            });
            return header + body;
          }
        },
        color: color,
        data: tempObjForFilterKey[item]
      };
      seriesData.push(temp);
    });
    
    const tempOptions = JSON.parse(JSON.stringify(options));
    // tempOptions.tooltip.formatter = tooltipFormatter;
    tempOptions.legend.data = legendData;
    tempOptions.xAxis.data = xAxisData;
    tempOptions.series = seriesData;
    return tempOptions;
  }, [dataForAge, filter, ageList]);

  useEffect(() => {
    getKey();
    getChartDataForCountry();
    getChartDataForAge();
  }, []);

  /**
   * export excel
   * format data
   */
  const exportExcel = useCallback((type = 'country') => {
    const excelData = [];
    let excelName = 'dataForCountry';
    let originData = null;
    if(type === 'country') {
      excelName = 'dataForCountry';
      originData = dataForCountry;
    } else {
      excelName = 'dataForAge';
      originData = dataForAge;
    }

    const nameKeys = Object.keys(originData);
    filter.forEach((item) => {
      const tempObj = {Name: item};
      nameKeys.forEach(subItem => {
        tempObj[subItem] = originData[subItem][item];
      });
      excelData.push(tempObj);
    });
    generateExcel(excelData, excelName);
  }, [dataForCountry, filter, dataForAge]);

  return {filter, countryList, dataForCountry, ageList, dataForAge, loadingForCountry, loadingForAge, getChartOptionsForCountry, getChartOptionsForAge, exportExcel};
};

export default usePopularDiet;